import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import classnames from 'classnames';
import dibsCss from 'dibs-css';

import { type PdpSharedSimilarSoldItemsHeader_item$key } from './__generated__/PdpSharedSimilarSoldItemsHeader_item.graphql.ts';

const itemFragment = graphql`
    fragment PdpSharedSimilarSoldItemsHeader_item on Item {
        categoryL3: getCategoriesByLevel(level: 3) {
            name
        }
        pdpDetailsDisplay {
            detailKey
            detailValues {
                displayName
            }
        }
    }
`;

export const PdpSharedSimilarSoldItemsHeader: FC<{
    item: PdpSharedSimilarSoldItemsHeader_item$key;
}> = ({ item: itemRef }) => {
    const item = useFragment(itemFragment, itemRef);

    const categoryL3 = item?.categoryL3?.[0]?.name;

    if (!categoryL3) {
        return null;
    }

    const creatorDisplayNames =
        item?.pdpDetailsDisplay?.find(detail => {
            const detailKey = detail?.detailKey;
            return detailKey && ['DESIGNER', 'CREATOR'].includes(detailKey);
        })?.detailValues || [];

    // don't show creator/designer at all if there is more than one
    const creatorDisplayName =
        creatorDisplayNames?.length === 1 ? creatorDisplayNames?.[0]?.displayName : null;

    return (
        <FormattedMessage
            id="global.pastSales"
            defaultMessage="Past Sales for {category}{creatorDisplayName, select, null {} other { by {creatorDisplayName}}}"
            values={{ category: categoryL3, creatorDisplayName }}
        />
    );
};

export const PdpSharedSimilarSoldItemsSubHeader: FC<{ isBelowMinPriceEstimate: boolean }> = ({
    isBelowMinPriceEstimate,
}) => (
    <FormattedMessage
        id="global.similarSoldItemsSubHeader"
        defaultMessage="Based on items with similar attributes, this item is {isBelowMinPriceEstimate, select, true {<green>Well Priced</green>} other {<green>Competitively Priced</green>}}."
        values={{
            isBelowMinPriceEstimate,
            green: chunks => (
                <span
                    className={classnames(
                        dibsCss.sassyFontBodySmallHeavy,
                        dibsCss.textSuccesssecondary
                    )}
                >
                    {chunks}
                </span>
            ),
        }}
    />
);
